import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';

// Een greep op de rand tussen twee onderdelen van de desktop (ticket 28376): de gesprekkenkolom, de verdeling tussen de panelen,
// het mailpaneel naast het gesprek en de werkerskolom. Een strook van 6 px over de rand; zelf neemt hij geen ruimte in (een vak
// van 0 px breed), zodat de onderdelen hun breedte en randlijn houden. Rol separator met de waarde voor een schermlezer.
// Slepen met muis, vinger of pen gaat met pointer events en setPointerCapture: de greep houdt de aanwijzer vast, ook buiten het
// venster. Tijdens het slepen staat er een accentlijn en geldt op de hele pagina de sleepcursor, zonder tekstselectie en zonder
// dat een iframe (de mail in het mailpaneel) de muis vangt (html[data-resizing] in styles/index.css). Pijltjes links en rechts
// verschuiven de rand 16 px (met Shift 64 px), Home en End zetten het onderdeel op zijn minimum en maximum, Enter en dubbelklik
// zetten de standaard terug. Elke tussenwaarde gaat naar `onChange` (alleen in beeld); bewaard wordt via `onCommit` bij het
// loslaten van de muis, bij het loslaten van een toets (een vastgehouden pijltje is één keer bewaren) en bij dubbelklik.
// Met de focus (Tab) staat de accentlijn er ook, in plaats van het focuskader van de app (`[data-resizer]` in index.css): een
// kader om een strook van 6 px over de volle hoogte ligt over beide onderdelen heen.
// Met `axis` y (ticket 28625: de lijst boven de lezer, de gevonden mails boven de mail) is het dezelfde greep liggend: een strook
// van 6 px over een horizontale rand zonder eigen hoogte, slepen op de hoogte, pijltjes omhoog en omlaag, en de cursor voor
// omhoog en omlaag (html[data-resizing='row']).

/** Zoveel px verschuift een pijltje de rand; met Shift RESIZE_STEP_BIG. */
export const RESIZE_STEP = 16;
export const RESIZE_STEP_BIG = 64;

export interface ResizerProps {
  /** Naam voor een schermlezer, zoals "Breedte gesprekkenkolom". */
  label: string;
  /** De waarde van nu en de grenzen bij de ruimte van nu, in eenheden: px, of procent bij de verdeling van de panelen. */
  value: number;
  min: number;
  max: number;
  /** Wat Enter en dubbelklik terugzetten. */
  defaultValue: number;
  /** De richting: `x` (standaard) voor een verticale rand tussen onderdelen naast elkaar, `y` voor een horizontale rand tussen
   * onderdelen boven elkaar. */
  axis?: 'x' | 'y' | undefined;
  /** Aan welke kant van de greep het onderdeel staat waar de waarde bij hoort: `left` is vóór de greep (links, of bij `axis` y
   * erboven: naar rechts of naar beneden slepen maakt het groter), `right` erna (rechts of eronder: naar links of naar boven
   * slepen maakt het groter). Zo hoeft een liggende greep geen eigen kanten. */
  side: 'left' | 'right';
  /** Hoeveel px één eenheid is: 1 voor een breedte in px; bij procent van beide panelen hun breedte gedeeld door 100. */
  unit?: number | undefined;
  onChange: (value: number) => void;
  onCommit: (value: number) => void;
  /** Wat een schermlezer bij de waarde zegt (aria-valuetext), zoals "280 pixels". */
  valueText?: ((value: number) => string) | undefined;
}

interface Drag {
  pointer: number;
  /** Waar de aanwijzer begon, langs de as (clientX of clientY). */
  at: number;
  start: number;
  value: number;
  moved: boolean;
}

export function Resizer(props: ResizerProps) {
  const unit = props.unit && props.unit > 0 ? props.unit : 1;
  const sign = props.side === 'left' ? 1 : -1;
  const lying = props.axis === 'y';
  /** De plek van de aanwijzer langs de as. */
  const along = (e: { clientX: number; clientY: number }) => (lying ? e.clientY : e.clientX);
  /** De pijltjes langs de as: het eerste schuift de rand terug (links of omhoog), het tweede vooruit. */
  const [back, forth] = lying ? ['ArrowUp', 'ArrowDown'] : ['ArrowLeft', 'ArrowRight'];
  const [dragging, setDragging] = useState(false);
  const drag = useRef<Drag | null>(null);
  /** Een waarde van de toetsen die nog bewaard moet worden: bij het loslaten van de toets, of als de greep de focus verliest. */
  const keyed = useRef<number | null>(null);
  const clamp = (v: number) => Math.min(props.max, Math.max(props.min, v));

  // De sleepcursor op de hele pagina zolang er gesleept wordt; weg aan het eind, ook als de greep tussendoor verdwijnt.
  useEffect(() => {
    if (!dragging) return;
    const root = document.documentElement;
    root.dataset['resizing'] = lying ? 'row' : '';
    return () => {
      delete root.dataset['resizing'];
    };
  }, [dragging, lying]);

  const start = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || drag.current) return;
    // Geen tekstselectie en geen focuswissel: wie typt, houdt de cursor in zijn vak.
    e.preventDefault();
    // In jsdom (de tests) bestaat setPointerCapture niet.
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { pointer: e.pointerId, at: along(e), start: props.value, value: props.value, moved: false };
    setDragging(true);
  };
  const move = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointer !== e.pointerId) return;
    const next = clamp(d.start + ((along(e) - d.at) * sign) / unit);
    if (next === d.value) return;
    d.value = next;
    d.moved = true;
    props.onChange(next);
  };
  /** Losgelaten, afgebroken of de greep kwijt: bewaren als er gesleept is (ook terug naar het begin, zodat de eigen waarde vervalt). */
  const finish = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointer !== e.pointerId) return;
    drag.current = null;
    setDragging(false);
    if (d.moved) props.onCommit(d.value);
  };

  const keyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const now = keyed.current ?? props.value;
    let next: number;
    if (e.key === back || e.key === forth) {
      const px = (e.shiftKey ? RESIZE_STEP_BIG : RESIZE_STEP) * (e.key === forth ? 1 : -1) * sign;
      next = clamp(now + px / unit);
    } else if (e.key === 'Home') next = props.min;
    else if (e.key === 'End') next = props.max;
    else if (e.key === 'Enter') next = props.defaultValue;
    else return;
    e.preventDefault();
    if (next === now) return;
    keyed.current = next;
    props.onChange(next);
  };
  const commitKey = () => {
    const v = keyed.current;
    if (v === null) return;
    keyed.current = null;
    props.onCommit(v);
  };
  const reset = () => {
    keyed.current = null;
    props.onCommit(props.defaultValue);
  };

  return (
    <div className={`relative ${lying ? 'h-0' : 'w-0'} shrink-0 z-20`}>
      <div
        role="separator"
        aria-orientation={lying ? 'horizontal' : 'vertical'}
        aria-label={props.label}
        aria-valuenow={Math.round(props.value)}
        aria-valuemin={Math.round(props.min)}
        aria-valuemax={Math.round(props.max)}
        aria-valuetext={props.valueText?.(props.value)}
        tabIndex={0}
        title={`${props.label}: slepen om ${lying ? 'hoger of lager te maken' : 'te verbreden of te versmallen'}, dubbelklik voor de standaard`}
        data-resizer=""
        data-dragging={dragging ? 'true' : undefined}
        className={`group absolute ${lying ? 'inset-x-0 -top-[3px] h-[6px] cursor-row-resize' : 'inset-y-0 -left-[3px] w-[6px] cursor-col-resize'} touch-none select-none outline-none`}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={finish}
        onPointerCancel={finish}
        onLostPointerCapture={finish}
        onKeyDown={keyDown}
        onKeyUp={commitKey}
        onBlur={commitKey}
        onDoubleClick={reset}
      >
        <span aria-hidden="true" className={`pointer-events-none absolute ${lying ? 'inset-x-0 top-[2px] h-[2px]' : 'inset-y-0 left-[2px] w-[2px]'} transition-colors duration-100 ${dragging ? 'bg-accent' : 'group-hover:bg-accent/40 group-focus-visible:bg-accent'}`} />
      </div>
    </div>
  );
}
